import { FileIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { formatFileSize } from '@/features/workspace-files/format';
import type {
  AttachmentStatus,
  StagedAttachment,
} from '../drafts/draftUploads';

/** A word for each state — status only, no progress bar (brand rule FD-16). */
function statusText(status: AttachmentStatus): string {
  switch (status.kind) {
    case 'refused':
      return status.reason;
    case 'waiting':
      return 'Waiting';
    case 'uploading':
      return 'Uploading…';
    case 'done':
      return 'Uploaded';
    case 'failed':
      return status.reason;
  }
}

/**
 * The files the Create dialog will attach, and then what became of each
 * (spec 120 FR-001 – FR-005). A file keeps its row whatever happens to it:
 * refused files say why and never go, failed ones offer Retry, and an
 * uploaded one shows the name the service stored it under — `x (2).png` when
 * the name was already taken.
 */
export function StagedAttachments({
  items,
  onRemove,
  onRetry,
}: {
  items: readonly StagedAttachment[];
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <ul aria-label="Attachments to upload" className="flex flex-col gap-1.5">
      {items.map((item) => {
        const { status } = item;
        const name =
          status.kind === 'done' ? status.storedName : item.file.name;
        const problem = status.kind === 'refused' || status.kind === 'failed';
        const removable =
          status.kind === 'refused' ||
          status.kind === 'waiting' ||
          status.kind === 'failed';
        return (
          <li
            key={item.id}
            className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-sm"
          >
            <FileIcon
              aria-hidden="true"
              className="text-muted-foreground size-4 shrink-0"
            />
            <span className="text-foreground min-w-0 [overflow-wrap:anywhere]">
              {name}
            </span>
            <span className="text-muted-foreground text-xs">
              {formatFileSize(item.file.size)}
            </span>
            <span
              className={`text-xs ${problem ? 'text-destructive' : 'text-muted-foreground'}`}
            >
              {statusText(status)}
            </span>
            <span className="ml-auto flex gap-1">
              {status.kind === 'failed' ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    onRetry(item.id);
                  }}
                >
                  Retry
                </Button>
              ) : null}
              {removable ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove ${item.file.name}`}
                  onClick={() => {
                    onRemove(item.id);
                  }}
                >
                  Remove
                </Button>
              ) : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
